import { useCallback, useMemo, useRef } from "react";

const AUTO_SCROLL_EDGE_THRESHOLD = 50; // px from edge to start scrolling
const AUTO_SCROLL_MAX_SPEED = 15; // max px per frame

interface UseAutoScrollParams {
  /** The element that scrolls vertically. If null, window scrolling is used. */
  scrollElementRef: React.RefObject<HTMLElement | null>;
  /** The element that scrolls horizontally. Defaults to scrollElementRef. */
  horizontalScrollElementRef?: React.RefObject<HTMLElement | null>;
  onScroll?: () => void;
}

function getVerticalEdges(element: HTMLElement | null) {
  if (element) {
    const rect = element.getBoundingClientRect();
    return { top: rect.top, bottom: rect.bottom };
  }
  return { top: 0, bottom: window.innerHeight };
}

function getHorizontalEdges(element: HTMLElement | null) {
  if (element) {
    const rect = element.getBoundingClientRect();
    return { left: rect.left, right: rect.right };
  }
  return { left: 0, right: window.innerWidth };
}

function computeAxisDelta(
  position: number,
  edgeStart: number,
  edgeEnd: number,
): number {
  const distFromStart = position - edgeStart;
  const distFromEnd = edgeEnd - position;

  if (distFromStart < 0) return -AUTO_SCROLL_MAX_SPEED;
  if (distFromEnd < 0) return AUTO_SCROLL_MAX_SPEED;
  if (distFromStart < AUTO_SCROLL_EDGE_THRESHOLD) {
    return -Math.ceil(
      (1 - distFromStart / AUTO_SCROLL_EDGE_THRESHOLD) * AUTO_SCROLL_MAX_SPEED,
    );
  }
  if (distFromEnd < AUTO_SCROLL_EDGE_THRESHOLD) {
    return Math.ceil(
      (1 - distFromEnd / AUTO_SCROLL_EDGE_THRESHOLD) * AUTO_SCROLL_MAX_SPEED,
    );
  }
  return 0;
}

export default function useAutoScroll({
  scrollElementRef,
  horizontalScrollElementRef,
  onScroll,
}: UseAutoScrollParams) {
  const hRef = horizontalScrollElementRef ?? scrollElementRef;

  const scrollAnimationRef = useRef<number | null>(null);
  const lastMousePositionRef = useRef<{
    clientX: number;
    clientY: number;
  } | null>(null);
  const isActiveRef = useRef(false);
  // Use ref for onScroll to avoid dependency cascades
  const onScrollRef = useRef(onScroll);
  onScrollRef.current = onScroll;

  const stopAutoScroll = useCallback(() => {
    if (scrollAnimationRef.current !== null) {
      cancelAnimationFrame(scrollAnimationRef.current);
      scrollAnimationRef.current = null;
    }
  }, []);

  const performAutoScroll = useCallback(() => {
    const mousePos = lastMousePositionRef.current;
    if (!mousePos || !isActiveRef.current) {
      stopAutoScroll();
      return;
    }

    const verticalEl = scrollElementRef.current;
    const horizontalEl = hRef.current;

    const { top, bottom } = getVerticalEdges(verticalEl);
    const { left, right } = getHorizontalEdges(horizontalEl);

    const scrollY = computeAxisDelta(mousePos.clientY, top, bottom);
    const scrollX = computeAxisDelta(mousePos.clientX, left, right);

    // Stop the animation loop when no scrolling is needed;
    // it will be restarted by the consumer when the mouse re-enters the edge zone.
    if (scrollX === 0 && scrollY === 0) {
      stopAutoScroll();
      return;
    }

    if (scrollY !== 0) {
      if (verticalEl) verticalEl.scrollBy(0, scrollY);
      else window.scrollBy(0, scrollY);
    }

    if (scrollX !== 0) {
      if (horizontalEl) horizontalEl.scrollBy(scrollX, 0);
      else window.scrollBy(scrollX, 0);
    }

    // Notify callback to update selection after scrolling
    if (onScrollRef.current) {
      queueMicrotask(onScrollRef.current);
    }

    // Continue animation loop
    scrollAnimationRef.current = requestAnimationFrame(performAutoScroll);
  }, [scrollElementRef, hRef, stopAutoScroll]);

  const startAutoScroll = useCallback(() => {
    if (scrollAnimationRef.current === null) {
      scrollAnimationRef.current = requestAnimationFrame(performAutoScroll);
    }
  }, [performAutoScroll]);

  const updateMousePosition = useCallback(
    (clientX: number, clientY: number) => {
      lastMousePositionRef.current = { clientX, clientY };
    },
    [],
  );

  const getMousePosition = useCallback(() => {
    return lastMousePositionRef.current;
  }, []);

  const setActive = useCallback((active: boolean) => {
    isActiveRef.current = active;
  }, []);

  const checkNeedsScroll = useCallback(
    (clientX: number, clientY: number): boolean => {
      const { top, bottom } = getVerticalEdges(scrollElementRef.current);
      const { left, right } = getHorizontalEdges(hRef.current);
      const scrollY = computeAxisDelta(clientY, top, bottom);
      const scrollX = computeAxisDelta(clientX, left, right);
      return scrollX !== 0 || scrollY !== 0;
    },
    [scrollElementRef, hRef],
  );

  return useMemo(
    () => ({
      startAutoScroll,
      stopAutoScroll,
      updateMousePosition,
      getMousePosition,
      setActive,
      checkNeedsScroll,
    }),
    [
      startAutoScroll,
      stopAutoScroll,
      updateMousePosition,
      getMousePosition,
      setActive,
      checkNeedsScroll,
    ],
  );
}
